import { fetchAdminUserClients } from '@/api/admin';
import ClientCard from '@/components/clients/cards/client-card';
import { Spinner } from '@/components/ui/spinner';
import type { IClient } from '@/entities/client.entity';
import { LoaderCircleIcon } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

interface ClientsTabProps {
	profileId: number;
}

export default function ClientsTab({ profileId }: ClientsTabProps) {
	const [clients, setClients] = useState<IClient[]>([]);
	const [nextCursor, setNextCursor] = useState<string | null>(null);
	const [hasMore, setHasMore] = useState(true);
	const [isFetching, setIsFetching] = useState(false);
	const [firstLoaded, setFirstLoaded] = useState(false);
	const fetchingRef = useRef(false);
	const sentinelRef = useRef<HTMLDivElement | null>(null);

	useEffect(() => {
		let cancelled = false;

		async function loadInitial() {
			setIsFetching(true);
			setFirstLoaded(false);
			try {
				const { clients: data, nextCursor: cursor } = await fetchAdminUserClients(profileId, { limit: 35 });
				if (cancelled) return;
				setClients(data.map(c => ({ ...c, cards: c.cards ?? [] })));
				setNextCursor(cursor);
				setHasMore(cursor !== null);
			} finally {
				if (!cancelled) {
					setIsFetching(false);
					setFirstLoaded(true);
				}
			}
		}

		loadInitial();
		return () => {
			cancelled = true;
		};
	}, [profileId]);

	async function loadMore() {
		if (fetchingRef.current || !hasMore || !nextCursor) return;
		fetchingRef.current = true;
		setIsFetching(true);
		try {
			const { clients: data, nextCursor: cursor } = await fetchAdminUserClients(profileId, {
				cursor: nextCursor,
				limit: 35,
			});
			setClients(prev => [...prev, ...data.map(c => ({ ...c, cards: c.cards ?? [] }))]);
			setNextCursor(cursor);
			setHasMore(cursor !== null);
		} finally {
			setIsFetching(false);
			fetchingRef.current = false;
		}
	}

	useEffect(() => {
		const el = sentinelRef.current;
		if (!el) return;

		const observer = new IntersectionObserver(
			entries => {
				if (entries[0]?.isIntersecting) loadMore();
			},
			{ rootMargin: '200px' },
		);
		observer.observe(el);
		return () => observer.disconnect();
	});

	if (!firstLoaded) {
		return (
			<div className='flex justify-center py-8'>
				<Spinner />
			</div>
		);
	}

	if (clients.length === 0) {
		return <div className='text-center text-muted-foreground text-sm py-8'>Клиентов нет</div>;
	}

	return (
		<div className='flex flex-col gap-2'>
			{clients.map(client => (
				<ClientCard key={client.id} client={client} />
			))}
			<div ref={sentinelRef} className='h-1' />
			{isFetching && (
				<div className='flex justify-center py-2'>
					<LoaderCircleIcon className='animate-spin text-muted-foreground' size={18} />
				</div>
			)}
		</div>
	);
}
